import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  resizer: {
    backgroundColor: 'var(--color-neutral-500)',
    transition: 'background-color 0.15s ease',
    userSelect: 'none',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'var(--color-primary-500)'
    }
  },
  resizerHorizontal: {
    width: '6px',
    cursor: 'col-resize',
    '&:hover': {
      width: '8px'
    }
  },
  resizerVertical: {
    height: '6px',
    cursor: 'row-resize',
    '&:hover': {
      height: '8px'
    }
  },
  resizing: {
    backgroundColor: 'var(--color-primary-500)',
    '&.horizontal': {
      width: '8px'
    },
    '&.vertical': {
      height: '8px'
    }
  },
  resizingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    cursor: 'col-resize',
    '&.vertical': {
      cursor: 'row-resize'
    }
  }
}))

export function ResizableHandle ({ direction = 'horizontal', onResize, onDoubleClick, className }) {
  const classes = useStyles()
  const [isResizing, setIsResizing] = useState(false)
  const lastPosRef = useRef(null)

  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    setIsResizing(true)

    if (direction === 'horizontal') {
      lastPosRef.current = e.clientX
    } else {
      lastPosRef.current = e.clientY
    }
  }, [direction])

  const handleDoubleClick = useCallback((e) => {
    e.preventDefault()
    if (onDoubleClick) {
      onDoubleClick()
    }
  }, [onDoubleClick])

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || lastPosRef.current === null) return

    const currentPos = direction === 'horizontal' ? e.clientX : e.clientY
    const delta = currentPos - lastPosRef.current

    if (onResize && delta !== 0) {
      onResize(delta)
      lastPosRef.current = currentPos
    }
  }, [isResizing, direction, onResize])

  const handleMouseUp = useCallback(() => {
    setIsResizing(false)
    lastPosRef.current = null
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  return (
    <>
      <div
        className={clsx(
          classes.resizer,
          direction === 'horizontal' ? classes.resizerHorizontal : classes.resizerVertical,
          isResizing && classes.resizing,
          isResizing && direction,
          className
        )}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        title='Double-click to reset size'
      />
      {isResizing && (
        <div
          className={clsx(
            classes.resizingOverlay,
            direction === 'vertical' && 'vertical'
          )}
        />
      )}
    </>
  )
}

ResizableHandle.propTypes = {
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  onResize: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func,
  className: PropTypes.string
}

export default ResizableHandle
